import React, {useCallback, useContext, useState} from "react";
import {Button, Dropdown, Menu, message, Modal, Popconfirm, Select, Space} from "antd";
import {BulkUpdateHierarchyModal} from "./BulkUpdateHierarchy";
import {DownOutlined} from "@ant-design/icons";
import Hierarchy from "../../ImageViewerHandlers/Hierarchy/Hierarchy";
import {ViewHierarchy} from "./ViewHierarchy";
import {BulkUpdateHierarchyFromScriptModal} from "./BulkUpdateHierarchyFromScriptModal";
import CacheService from "../../../services/CacheService";
import {AnimatedAuthUser} from "../../../Utilities";
import {AuthContext} from "../../../contexts/AuthContext";

export function ViewHierarchyActions({hierarchy, viewHierarchy}: {
    hierarchy?: Hierarchy,
    viewHierarchy: ViewHierarchy,
}) {
    const wsId = useCallback(() => CacheService.getProjectData(viewHierarchy.getProjectId()).then(
        projectData => projectData.workspace_id
    ), [viewHierarchy]);
    const authContext = useContext(AuthContext);
    const user = new AnimatedAuthUser(authContext.user);

    const [isLoading, setLoading] = useState(false);
    const [bulkUpdateModalVisible, setBulkUpdateModalVisible] = useState(false);
    const [bulkUpdateFromScriptModalVisible, setBulkUpdateFromScriptModalVisible] = useState(false);
    const [uniqueKeysModalVisible, setUniqueKeysModalVisible] = useState(false);
    const [uniqueKeysValue, setUniqueKeysValue] = useState<string[]>([]);

    const actionsMenu = (
        <Menu>
            {hierarchy != null && (
                <Menu.Item key="toggle-hierarchy">
                    <Popconfirm
                        title={hierarchy.hidden ? "Are you sure to unhide hierarchy?" : "Are you sure to hide hierarchy?"}
                        onConfirm={() =>
                            hierarchy
                                .setHiddenStatus(!hierarchy.hidden)
                                .then(() => {
                                    viewHierarchy.softReload();
                                    message.success("Success");
                                    viewHierarchy.props.imageViewer.hierarchyListChanged$.next()
                                })
                                .catch(() => message.error("Failed to switch"))
                        }
                    >
                        <div style={{ width: '100%' }}>
                            <Space>
                                {hierarchy.hidden ? "Unhide" : "Hide"}
                            </Space>
                        </div>
                    </Popconfirm>
                </Menu.Item>
            )}

            <Menu.Divider/>
            {user.isAdmin() && hierarchy != null && (
                <Menu.Item key="unique-attr-keys" onClick={() => { setUniqueKeysValue([...(hierarchy?.uniqueAttributeKeys ?? [])]); setUniqueKeysModalVisible(true); }}>
                    Unique attribute keys
                </Menu.Item>
            )}
            <Menu.Item onClick={() => setBulkUpdateModalVisible(true)}>
                Bulk Update
            </Menu.Item>
            <Menu.Item onClick={() => setBulkUpdateFromScriptModalVisible(true)}>
                Custom Bulk Update
            </Menu.Item>

            <Menu.Divider/>
            {user.isAdmin() && hierarchy != null && !hierarchy.useV2References && (
                <Menu.Item
                    disabled={isLoading}
                >
                    <Popconfirm
                        style={{display: 'block'}}
                        title="Are you sure to switch to references v2?"
                        onConfirm={() => hierarchy.setUseRefsV2(true).then(() => {
                            hierarchy.hierarchyView.softReload();
                            message.success("Success");
                        }).catch(() => message.error("Failed to switch"))}
                    >
                        Switch to references v2
                    </Popconfirm>
                </Menu.Item>
            )}

            {user.isAdmin() && hierarchy != null && hierarchy.useV2References && (
                <Menu.Item
                    disabled={isLoading}
                >
                    <Popconfirm
                        style={{display: 'block'}}
                        title="Are you sure to switch to references v1?"
                        onConfirm={() => hierarchy.setUseRefsV2(false).then(() => {
                            hierarchy.hierarchyView.softReload();
                            message.success("Success");
                        }).catch(() => message.error("Failed to switch"))}
                    >
                        Switch to references v1
                    </Popconfirm>
                </Menu.Item>
            )}

            {hierarchy != null && hierarchy.useV2References && !hierarchy.isInScopeOfTheCurrentPage && (
                <Menu.Item
                    disabled={isLoading}
                    onClick={() => hierarchy.addCurrentPageToScope().then(() => {
                        hierarchy.hierarchyView.softReload();
                        message.success("Success");
                    }).catch(() => message.error("Failed to add to scope"))}
                >
                    Add this page to scope
                </Menu.Item>
            )}

            {hierarchy != null && hierarchy.useV2References && hierarchy.isInScopeOfTheCurrentPage && (
                <Menu.Item
                    disabled={isLoading}
                    onClick={() => hierarchy.removeCurrentPageFromScope().then(() => {
                        hierarchy.hierarchyView.softReload();
                        message.success("Success");
                    }).catch(() => message.error("Failed to remove from scope"))}
                >
                    Remove this page from scope
                </Menu.Item>
            )}
        </Menu>
    );

    return (
        <>
            <Dropdown overlay={actionsMenu} trigger={["click"]}>
                <Button>Actions <DownOutlined/></Button>
            </Dropdown>
            {hierarchy != null && (
              <>
                  <Modal
                      title="Unique attribute keys — names for which (name, value) must be unique across nodes."
                      visible={uniqueKeysModalVisible}
                      onCancel={() => setUniqueKeysModalVisible(false)}
                      onOk={() => hierarchy.setUniqueAttributeKeys(uniqueKeysValue).then(() => {
                          viewHierarchy.reloadHierarchy();
                          message.success("Saved");
                          setUniqueKeysModalVisible(false);
                      }).catch(() => message.error("Failed to save"))}
                      okText="Save"
                  >
                      <Select mode="tags" style={{ width: "100%" }} placeholder="e.g. floc" value={uniqueKeysValue} onChange={setUniqueKeysValue} />
                  </Modal>
                  <BulkUpdateHierarchyModal
                      onOk={() => viewHierarchy.reloadHierarchy()}
                      hierarchy={hierarchy}
                      visible={bulkUpdateModalVisible}
                      setVisible={setBulkUpdateModalVisible}
                  />
                  <BulkUpdateHierarchyFromScriptModal
                      wsId={wsId}
                      onOk={() => viewHierarchy.reloadHierarchy()}
                      hierarchy={hierarchy}
                      visible={bulkUpdateFromScriptModalVisible}
                      setVisible={setBulkUpdateFromScriptModalVisible}
                  />
              </>
            )}
        </>
    );
}
